import React from 'react';
import { Info, ChevronDown, ChevronUp, Shield, Lock, Key, Code, FileCode, Server, Database } from 'lucide-react';

/**
 * Информационный контент о Bcrypt и безопасном хранении паролей
 */
const BcryptInfoContent = ({ showHashInfo, setShowHashInfo }) => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        О Bcrypt-хешировании и безопасном хранении паролей
      </h2>
      
      <div className="prose prose-blue dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-500" />
          Что такое Bcrypt?
        </h3>
        <p>
          <strong>Bcrypt</strong> — это криптографическая хеш-функция для паролей, разработанная Нильсом Провосом и Дэвидом Мазьером. 
          Она является одним из наиболее надежных алгоритмов хеширования паролей, широко используемых в веб-приложениях и системах 
          безопасности в России и по всему миру.
        </p>
        <p>
          В отличие от обычных хеш-функций (таких как MD5 или SHA), Bcrypt специально разработан для <strong>медленного</strong> хеширования, 
          что защищает от атак перебором. Это достигается за счет возможности регулировать количество "раундов" (итераций) 
          вычислений, что позволяет увеличивать вычислительную сложность с ростом мощности оборудования злоумышленников.
        </p>

        <h3 className="flex items-center gap-2">
          <Key className="w-5 h-5 text-blue-500" />
          Ключевые преимущества Bcrypt
        </h3>
        <ul>
          <li><strong>Встроенная "соль" (salt):</strong> Bcrypt автоматически генерирует и сохраняет криптографическую соль вместе с хешем, что защищает от атак с помощью радужных таблиц.</li>
          <li><strong>Регулируемая сложность:</strong> Параметр "раундов" позволяет увеличивать вычислительную сложность хеширования, чтобы оставаться на шаг впереди растущих вычислительных мощностей.</li>
          <li><strong>Проверенная временем:</strong> Используется с 1999 года и до сих пор остается одним из рекомендуемых алгоритмов для хеширования паролей.</li>
          <li><strong>Широкая поддержка:</strong> Реализован в большинстве языков программирования и фреймворков.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Рекомендации для российских разработчиков</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
            В соответствии с рекомендациями по информационной безопасности в России, для систем, обрабатывающих персональные данные, 
            рекомендуется использовать стойкие алгоритмы хеширования. Bcrypt полностью соответствует этим требованиям, обеспечивая:
          </p>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0 list-disc pl-5">
            <li>Надёжную защиту от перебора паролей</li>
            <li>Автоматическое "соление" (добавление случайных данных)</li>
            <li>Настраиваемую вычислительную сложность</li>
            <li>Кроссплатформенную совместимость</li>
          </ul>
        </div>

        {/* Секция, которую можно раскрыть */}
        <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowHashInfo(!showHashInfo)}
            className="w-full p-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 text-left"
          >
            <span className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <Lock className="w-5 h-5 mr-2 text-blue-500" />
              Структура Bcrypt-хеша и технические детали
            </span>
            {showHashInfo ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          {showHashInfo && (
            <div className="p-4">
              <h4 className="text-lg font-medium mb-3 text-gray-900 dark:text-white">Анатомия Bcrypt-хеша</h4>
              <p className="mb-3">
                Bcrypt-хеш имеет следующий формат:
              </p>
              <div className="bg-gray-100 dark:bg-gray-800 p-3 font-mono text-sm rounded-md mb-4 overflow-x-auto">
                <span className="text-blue-600 dark:text-blue-400">$2a$10$</span>
                <span className="text-green-600 dark:text-green-400">N9qo8uLOickgx2ZMRZoMye</span>
                <span className="text-purple-600 dark:text-purple-400">IjZAgcfl7p92ldGxad68LJZdL17lhWy</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs mb-4">
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-1.5"></span>
                  <span className="text-gray-600 dark:text-gray-400">Алгоритм и стоимость</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1.5"></span>
                  <span className="text-gray-600 dark:text-gray-400">Соль (22 символа)</span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1.5"></span>
                  <span className="text-gray-600 dark:text-gray-400">Хеш пароля (31 символ)</span>
                </div>
              </div>
              
              <p className="mb-3">Где:</p>
              <ul className="mb-4">
                <li className="mb-2">
                  <strong className="text-blue-600 dark:text-blue-400">$2a$</strong> — идентификатор версии алгоритма:
                  <ul className="ml-6 mt-1">
                    <li><strong>$2a$</strong> — стандартная версия</li>
                    <li><strong>$2b$</strong> — улучшенная версия, исправляющая некоторые ошибки</li>
                    <li><strong>$2y$</strong> — вариант, используемый в PHP-реализациях</li>
                  </ul>
                </li>
                <li className="mb-2">
                  <strong className="text-blue-600 dark:text-blue-400">10</strong> — фактор стоимости (число раундов), 
                  определяющий сложность хеширования. Представлен в виде числа от 04 до 31, где каждое увеличение на 1 
                  удваивает время работы функции.
                </li>
                <li className="mb-2">
                  <strong className="text-green-600 dark:text-green-400">N9qo8uLOickgx2ZMRZoMye</strong> — соль (22 символа), 
                  случайное значение, добавляемое к паролю перед хешированием для предотвращения атак с помощью предварительно 
                  вычисленных таблиц.
                </li>
                <li>
                  <strong className="text-purple-600 dark:text-purple-400">IjZAgcfl7p92ldGxad68LJZdL17lhWy</strong> — собственно 
                  хеш пароля (31 символ).
                </li>
              </ul>
              
              <h5 className="font-medium mb-2 text-gray-900 dark:text-white">Принцип работы Bcrypt</h5>
              <p>
                Bcrypt основан на шифре Blowfish и использует адаптивную функцию для увеличения 
                сложности с течением времени. Процесс работы:
              </p>
              <ol className="list-decimal list-inside mb-4 pl-2">
                <li className="mb-1">Генерация случайной соли (22 символа)</li>
                <li className="mb-1">Комбинирование соли с паролем</li>
                <li className="mb-1">Запуск функции шифрования Blowfish заданное количество раундов (2^стоимость)</li>
                <li className="mb-1">Формирование финального хеша в формате: $версия$стоимость$соль+хеш</li>
              </ol>
              
              <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Примеры кода</h4>
              <p className="mb-3">Примеры использования Bcrypt на разных языках программирования:</p>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <FileCode className="w-4 h-4 mr-2 text-blue-500" />
                  <span className="text-sm font-medium">JavaScript (Node.js)</span>
                </div>
                <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md overflow-x-auto">
                  <code className="text-sm">
{`const bcrypt = require('bcrypt');

// Хеширование пароля
const saltRounds = 10;
const password = 'безопасный_пароль';

// Генерация хеша
bcrypt.hash(password, saltRounds, (err, hash) => {
  // Сохраняем hash в базе данных
  console.log(hash);
});

// Проверка пароля
bcrypt.compare(password, hash, (err, result) => {
  if (result) {
    // Пароль верный
  } else {
    // Пароль неверный
  }
});`}
                  </code>
                </pre>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <FileCode className="w-4 h-4 mr-2 text-blue-500" />
                  <span className="text-sm font-medium">Python</span>
                </div>
                <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md overflow-x-auto">
                  <code className="text-sm">
{`import bcrypt

# Хеширование пароля
password = 'безопасный_пароль'.encode()
salt = bcrypt.gensalt(rounds=10)
hashed = bcrypt.hashpw(password, salt)

# Сохраняем hashed в базе данных
print(hashed)

# Проверка пароля
if bcrypt.checkpw(password, hashed):
    # Пароль верный
else:
    # Пароль неверный`}
                  </code>
                </pre>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center mb-2">
                  <FileCode className="w-4 h-4 mr-2 text-blue-500" />
                  <span className="text-sm font-medium">PHP</span>
                </div>
                <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md overflow-x-auto">
                  <code className="text-sm">
{`<?php
// Хеширование пароля
$password = 'безопасный_пароль';
$options = ['cost' => 10];
$hashed = password_hash($password, PASSWORD_BCRYPT, $options);

// Сохраняем $hashed в базе данных
echo $hashed;

// Проверка пароля
if (password_verify($password, $hashed)) {
    // Пароль верный
} else {
    // Пароль неверный
}
?>`}
                  </code>
                </pre>
              </div>
            </div>
          )}
        </div>

        <h3 className="flex items-center gap-2 mt-6">
          <Database className="w-5 h-5 text-blue-500" />
          Рекомендации по внедрению
        </h3>
        
        <div className="mb-4">
          <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Как правильно хранить пароли</h4>
          <ol className="list-decimal list-inside mb-4 pl-2">
            <li className="mb-1"><strong>Никогда не храните пароли в открытом виде</strong> — всегда используйте хеширование.</li>
            <li className="mb-1"><strong>Используйте современные алгоритмы</strong> — Bcrypt, Argon2, или PBKDF2.</li>
            <li className="mb-1"><strong>Обновляйте фактор стоимости</strong> — с ростом вычислительных мощностей увеличивайте количество раундов.</li>
            <li className="mb-1"><strong>Правильно настраивайте раунды</strong> — в 2024 году рекомендуется использовать минимум 10-12 раундов.</li>
            <li className="mb-1"><strong>Используйте дополнительные меры защиты</strong> — двухфакторную аутентификацию, ограничение попыток входа и т.д.</li>
          </ol>
        </div>
        
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-4">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <Server className="w-4 h-4" />
            Особенности внедрения в российских системах
          </h4>
          <ul className="text-yellow-700 dark:text-yellow-300 text-sm pl-5 list-disc mb-0">
            <li>При работе с персональными данными российских граждан необходимо соблюдать требования ФЗ-152 "О персональных данных"</li>
            <li>Рекомендуется дополнительно шифровать хеши в базе данных согласно ГОСТ Р 34.12-2015</li>
            <li>В системах с повышенными требованиями безопасности рассмотрите использование сертифицированных ФСБ средств криптографической защиты информации</li>
            <li>Документируйте использование алгоритмов хеширования в политике безопасности организации</li>
          </ul>
        </div>

        <p className="font-semibold mt-6">
          Этот генератор Bcrypt-хешей поможет вам создавать безопасные хеши для тестирования и разработки. Для производственного использования
          рекомендуется интегрировать Bcrypt непосредственно в ваше приложение.
        </p>
      </div>
    </article>
  );
};

export default BcryptInfoContent;